/* eslint-disable import/no-named-as-default */
import React, { Component, Fragment, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Pie from "../../Charts/Pie";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import { Wrapper } from "../styles/charts-styles";

import colorPalette from "../colorPalette/colorPalette";
import { ChartTitle } from "../styles/employer-analytics-styles";
import ReactTooltip from "react-tooltip";

export interface ICandidateSubmissionProps {
  candidateSubmission: {
    sectors?: Array<string>;
    values?: Array<number>;
  };
  candidateSubmissionWidgetLoader: boolean;
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
  globals: {
    seriesTotals: {
      reduce: (a, b) => number;
    };
  };
}

export class CandidateSubmission extends Component<ICandidateSubmissionProps> {
  static propTypes = {
    candidateSubmission: PropTypes.shape({
      sectors: PropTypes.arrayOf(PropTypes.string),
      values: PropTypes.arrayOf(PropTypes.number),
    }),
    candidateSubmissionWidgetLoader: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    candidateSubmission: {
      sectors: [],
      values: [],
    },
  };

  render(): ReactNode {
    const { candidateSubmission, candidateSubmissionWidgetLoader } = this.props;

    const candidateSubmissionOptions = {
      series: candidateSubmission.values,
      options: {
        width: 330,
        height: 330,
        chart: {
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 4,
            opacity: 0.1,
          },
        },
        colors: colorPalette(candidateSubmission.sectors.length),
        labels: candidateSubmission.sectors,
        dataLabels: {
          enabled: false, // This hides data labels on pie slices
        },
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            size: "100%",
            customScale: 1,
            offsetX: 0,
            offsetY: 20,
            expandOnClick: false,
            dataLabels: {
              offset: 0,
              minAngleToShowLabel: 10,
            },
            donut: {
              background: "transparent",
              labels: {
                show: false,
                value: {
                  show: true,
                  fontSize: "25px",
                  offsetY: 0,
                  formatter: (val: string) => val,
                },
                total: {
                  show: false,
                  showAlways: true,
                  label: "Total",
                  fontSize: "18px",
                  fontFamily: "Komet, sans-serif",
                  color: "#333",
                  formatter: (w: IFormatterProps) => {
                    const value = w.globals.seriesTotals.reduce(
                      (a: number, b: number) => a + b,
                      0
                    );
                    return `${value}`;
                  },
                },
              },
            },
          },
        },
        tooltip: {
          enabled: true,
          style: {
            fontSize: "14px",
            fontFamily: "Komet, sans-serif",
          },
          x: {
            title: {
              formatter: (seriesName) => seriesName as unknown as string,
            },
          },
          y: {
            formatter: (value: number) => `${value}`,
          },
        },
        responsive: [
          {
            breakpoint: 1199,
            options: {
              chart: {
                width: 325,
                height: 325,
              },
              legend: {
                show: false,
              },
              dataLabels: {
                style: {
                  fontSize: "12px",
                },
              },
            },
          },
        ],
      },
    };

    return (
      <Fragment>
        <Wrapper
          className="animated fadeIn delay-5"
          id="candidate-submission-widget"
        >
          <AnalyticsWidgetLoading
            text="Updating..."
            active={candidateSubmissionWidgetLoader}
          />

          <Pie
            graphOptions={candidateSubmissionOptions}
            dataTestId="candidate-submission-pie-chart"
          />
          <ChartTitle
            marginBottom={20}
            data-test-id="candidate-submission-chart-title"
            data-tip="Total number of candidates submitted. "
            data-for="candidate-submission-chart-title-tooltip"
          >
            <ReactTooltip
              type="dark"
              aria-haspopup="true"
              id="candidate-submission-chart-title-tooltip"
              className="tooltip-max-width"
            />
            Candidate Submission Summary
          </ChartTitle>
        </Wrapper>
      </Fragment>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    jobPerformanceData: {
      candidateSubmission: {
        sectors?: Array<string>;
        values?: Array<number>;
      };
    };
    candidateSubmissionWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  candidateSubmission:
    state.employerAnalytics.jobPerformanceData.candidateSubmission,
  candidateSubmissionWidgetLoader:
    state.employerAnalytics.candidateSubmissionWidgetLoader,
});

export default connect(mapStateToProps, {})(CandidateSubmission);
